import { Component, type ErrorInfo, type ReactNode } from "react";

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|dynamically imported module|ChunkLoadError/i;

const RELOAD_GUARD_KEY = "app-chunk-reload-at";
const RELOAD_GUARD_WINDOW_MS = 10_000;

/**
 * One reload per ~10s window. After a deploy the old lazy chunks are gone, so a
 * dynamic import fails; a single reload fetches the fresh index.html and fixes
 * it. The guard stops a reload loop if the chunk is genuinely broken.
 */
const reloadOnceGuarded = (): boolean => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? "0");
    if (Number.isFinite(last) && Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      return false;
    }
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage blocked — still worth one reload attempt.
  }
  window.location.reload();
  return true;
};

const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_PATTERN.test(message);
};

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  mode: "ok" | "reloading" | "failed";
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { mode: "ok" };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { mode: isChunkLoadError(error) ? "reloading" : "failed" };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Unhandled render error", error, info.componentStack);

    if (isChunkLoadError(error) && !reloadOnceGuarded()) {
      this.setState({ mode: "failed" });
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.mode === "ok") {
      return this.props.children;
    }

    if (this.state.mode === "reloading") {
      return null;
    }

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          textAlign: "center",
          background: "#0b1220",
          color: "#e2e8f0",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div>
          <p style={{ marginBottom: "1rem", fontSize: "0.95rem" }}>
            Что-то пошло не так при загрузке страницы.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "0.5rem 1.25rem",
              borderRadius: 8,
              border: "1px solid #334155",
              background: "#1e293b",
              color: "#e2e8f0",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            Обновить
          </button>
        </div>
      </div>
    );
  }
}
